import { useMemo, useState } from "react";
import { format } from "date-fns";
import { CoroaDentariaIcon } from "@/components/icons/CoroaDentariaIcon";
import { trpc } from "@/lib/trpc";
import { DENTIST_STATUS_META, dentistStatusBucket } from "@/components/prosthesis/dentist-status";
import { TERMINAL_STATUSES } from "@/components/prosthesis/prosthesis-meta";
import { ProsthesisOrderDetailDialog } from "@/components/prosthesis/ProsthesisOrderDetailDialog";
import type { ProsthesisOrderRow } from "@/components/prosthesis/types";
import { AcaoDoRodape, Card, CardEmpty, CardLoading, Rodape, VerTodos, linhaDoQuadro } from "./QuadroBase";
import type { QuadroProps } from "./tipos";

const prazo = (d: Date | string | null) => (d ? new Date(d).getTime() : Number.POSITIVE_INFINITY);

/** Os pedidos de prótese do paciente: em aberto primeiro (prazo mais perto), depois os encerrados. */
export function QuadroProteses({ patientId, menu, irParaAba, onNovo }: QuadroProps & { onNovo?: () => void }) {
  const query = trpc.prosthesis.list.useQuery({ patientId, take: 50, skip: 0 });
  const [aberto, setAberto] = useState<string | null>(null);

  const pedidos = useMemo(() => {
    const todos = (query.data?.orders ?? []) as ProsthesisOrderRow[];
    const emAberto = todos.filter((o) => !TERMINAL_STATUSES.includes(o.status)).sort((a, b) => prazo(a.deadline) - prazo(b.deadline));
    const encerrados = todos.filter((o) => TERMINAL_STATUSES.includes(o.status));
    return [...emAberto, ...encerrados].slice(0, 4);
  }, [query.data]);

  return (
    <Card icon={<CoroaDentariaIcon className="h-4 w-4" />} title="Pedidos de prótese" tone="fuchsia" menu={menu}>
      {query.isLoading ? (
        <CardLoading />
      ) : pedidos.length === 0 ? (
        <CardEmpty icon={<CoroaDentariaIcon className="h-6 w-6" />} text="Nenhum pedido de prótese" hint="Os pedidos deste paciente aparecerão aqui" />
      ) : (
        <ul className="space-y-2.5">
          {pedidos.map((o) => {
            const meta = DENTIST_STATUS_META[dentistStatusBucket(o.status)];
            const servicos = o.services.map((s) => s.serviceCategory?.label).filter(Boolean);
            return (
              <li key={o.id}>
                <button type="button" onClick={() => setAberto(o.id)} className={linhaDoQuadro}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-[11px] font-medium text-zinc-500 dark:text-muted-foreground">
                        {o.labName ?? "Sem laboratório"}
                        {o.deadline && ` · prazo ${format(new Date(o.deadline), "dd/MM")}`}
                      </p>
                      <span
                        className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                        style={{ backgroundColor: meta.bg, color: meta.color }}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-sm font-medium text-zinc-900 dark:text-foreground">
                      {servicos.length ? servicos.join(" · ") : o.description || "Pedido de prótese"}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Rodape>
        <VerTodos rotulo="Ver todos os pedidos" onClick={() => irParaAba("proteses")} />
        {onNovo ? <AcaoDoRodape rotulo="Novo pedido" onClick={onNovo} /> : <span />}
      </Rodape>
      <ProsthesisOrderDetailDialog orderId={aberto} onClose={() => setAberto(null)} readOnly />
    </Card>
  );
}
